import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartDataPoint } from '../types/AssetClasses';

interface TotalPortfolioChartProps {
  data: ChartDataPoint[];
  isLoading: boolean;
}

const TotalPortfolioChart: React.FC<TotalPortfolioChartProps> = ({ data, isLoading }) => {
  const [isLogScale, setIsLogScale] = useState<boolean>(false);
  
  if (isLoading) {
    return (
      <div className="chart-container">
        <h2>Total Portfolio Value Over Time</h2>
        <div className="loading">Running Monte Carlo simulation...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="chart-container">
        <h2>Total Portfolio Value Over Time</h2>
        <div className="no-data">No simulation data available. Please run a simulation.</div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatYAxisValue = (value: number) => {
    if (isLogScale) {
      if (value <= 0) {
        return '$0';
      } else if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      } else {
        return `$${value.toFixed(0)}`;
      }
    } else {
      return formatCurrency(value);
    }
  };

  // Filter data to handle zero values for log scale
  const getChartData = () => {
    if (!isLogScale) {
      return data;
    }
    
    return data.map(point => ({
      ...point,
      p10: point.p10 <= 0 ? null : point.p10,
      p50: point.p50 <= 0 ? null : point.p50,
    }));
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const validEntries = payload.filter((entry: any) => entry.value !== null && entry.value !== undefined).reverse();
      
      if (validEntries.length === 0) {
        return (
          <div className="custom-tooltip">
            <p className="tooltip-label">{`Year ${label}`}</p>
            <p style={{ color: '#718096', fontStyle: 'italic' }}>Portfolio depleted (all values at or below $0)</p>
          </div>
        );
      }

      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`Year ${label}`}</p>
          {validEntries.map((entry: any, index: number) => {
            const originalPoint = data[label - 1];
            const originalValue = originalPoint ? originalPoint[entry.dataKey as keyof ChartDataPoint] as number : entry.value;
            return (
              <p key={index} style={{ color: entry.color }}>
                {`${entry.name}: ${formatCurrency(originalValue)}`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h2>Total Portfolio Value Over Time</h2>
        <div className="chart-controls">
          <label className="scale-toggle">
            <input
              type="checkbox"
              checked={isLogScale}
              onChange={(e) => setIsLogScale(e.target.checked)}
            />
            <span>Logarithmic Scale</span>
          </label>
        </div>
      </div>
      <p className="chart-description">
        This chart shows the distribution of total portfolio values over time based on Monte Carlo simulation.
        Each line represents a different percentile of outcomes across all account types combined.
        {isLogScale && (
          <span className="log-scale-note">
            {' '}Logarithmic scale helps visualize percentage changes more clearly. Lines break when portfolio values reach zero.
          </span>
        )}
      </p>
      
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={500}>
          <LineChart
            data={getChartData()}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              label={{ value: 'Year', position: 'insideBottom', offset: -10 }}
              tickFormatter={(value) => `${value}`}
            />
            <YAxis 
              width={110}
              tickMargin={8}
              label={{ value: 'Total Portfolio Value', angle: -90, position: 'insideLeft', offset: 0, style: { textAnchor: 'middle' }  }}
              tickFormatter={formatYAxisValue}
              scale={isLogScale ? 'log' : 'linear'}
              domain={isLogScale ? ['dataMin', 'dataMax'] : ['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            <Line
              type="monotone"
              dataKey="p10"
              stroke="#ff8844"
              strokeWidth={2}
              name="10th Percentile"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="p50"
              stroke="#44aa44"
              strokeWidth={3}
              name="50th Percentile (Median)"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-legend">
        <h3>Percentile Explanation</h3>
        <ul>
          <li><strong>10th Percentile:</strong> 90% of simulations performed better than this line</li>
          <li><strong>50th Percentile (Median):</strong> Half of simulations performed better, half worse</li>
        </ul>
      </div>
    </div>
  );
};

export default TotalPortfolioChart;
